import { useEffect, useMemo, useRef, useState } from "react";
import { BarChart } from "../../../components/custom/charts/bar-chart";
import OrderService from "../../order-list/services/order-service";

const TIME_OPTIONS = [
  { label: "7 ngày", value: 7 },
  { label: "15 ngày", value: 15 },
  { label: "30 ngày", value: 30 },
  { label: "90 ngày", value: 90 },
] as const;

type DaysOption = 7 | 15 | 30 | 90;
type ReportData = { date: string; total_amount: number };

export const RevenueTimeReport = () => {
  const [selectedDays, setSelectedDays] = useState<DaysOption>(7);
  const [dataReports, setDataReports] = useState<ReportData[]>([]);
  const { fetch: listOrders, loading } = OrderService.list();
  const listOrdersRef = useRef(listOrders);

  useEffect(() => {
    listOrdersRef.current = listOrders;
  }, [listOrders]);

  useEffect(() => {
    const formatDate = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;

    const formatDisplayDate = (s: string) => {
      const [y, m, d] = s.split("-");
      return `${d}/${m}/${y}`;
    };

    let mounted = true;
    (async () => {
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setDate(toDate.getDate() - selectedDays + 1);

      const res = await listOrdersRef.current({
        from_date: formatDate(fromDate),
        to_date: formatDate(toDate),
        statuses: 5,
        limit: 500,
      });

      if (!mounted) return;
      if (res?.status === 200) {
        const orders = (res.data as any)?.list || [];
        const revenueByDate: Record<string, number> = {};

        // Khởi tạo mốc ngày với 0 doanh thu
        for (let d = new Date(fromDate); d <= toDate; d.setDate(d.getDate() + 1)) {
          revenueByDate[formatDate(new Date(d))] = 0;
        }

        orders.forEach((order: any) => {
          if (!order.created_at) return;
          const created = new Date(order.created_at);
          if (isNaN(created.getTime())) return;
          const key = formatDate(created);
          revenueByDate[key] = (revenueByDate[key] || 0) + Number(order.total_amount || 0);
        });

        const reports = Object.entries(revenueByDate).map(([date, total_amount]) => ({
          date: formatDisplayDate(date),
          total_amount,
        }));

        console.log("Revenue reports:", reports); // 👈 kiểm tra dữ liệu thực tế

        setDataReports(reports);
      } else {
        setDataReports([]);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [selectedDays]);

  const { chartData, chartCategories, totalRevenue } = useMemo(() => {
    const total = dataReports.reduce((sum, report) => sum + report.total_amount, 0);
    return {
      chartData: [{ name: "Doanh thu", data: dataReports.map((d) => d.total_amount) }],
      chartCategories: dataReports.map((d) => d.date),
      totalRevenue: total,
    };
  }, [dataReports]);

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm dark:bg-gray-900">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-baseline gap-3">
          <h3 className="font-semibold text-lg">Doanh thu theo thời gian :</h3>
          <span className="font-bold text-xl text-green-600">{totalRevenue.toLocaleString('vi-VN')}₫</span>
        </div>
        <div className="space-x-2">
          {TIME_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSelectedDays(opt.value)}
              className={`px-4 py-1 rounded-md border ${
                selectedDays === opt.value
                  ? "bg-blue-600 text-white"
                  : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-72 bg-gray-100 rounded-md flex items-center justify-center dark:bg-gray-800">
        {!loading && dataReports.some((d) => d.total_amount > 0) ? (
          <BarChart
            chartData={chartData}
            chartCategories={chartCategories}
            chartColors={["#4361ee"]}
            chartHeight={280}
            chartLoading={loading}
          />
        ) : (
          <p className="text-gray-400">Chưa có dữ liệu</p>
        )}
      </div>
    </div>
  );
};
